import { QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { BoardApi } from "../../../services/board-service-api";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaSelectbox from "../../ui-kit/kuika-selectbox/kuika-selectbox";
import { boardKeys } from "./board-query-keys";
import { BoardService } from "./board-service";
import BurndownChart from "./burndown-chart";
import { reportsChanged } from "./change-event-routing";
import "./board-reports-view.scss";
import {
  Board,
  BoardChangeEvent,
  BoardReport,
  boardProjectKeys,
  PRIORITY_COLOR,
  Sprint,
  SprintBurndown,
  STATUS_DOT_COLOR
} from "./types";
import { useBoardConfig } from "./use-board";

export interface BoardReportsViewProps {
  board: Board;
}

// Reports are computed on the backend (GET /board/reports/{boardId}) over the whole
// board (its projects), not the transient quick-filters — this view only renders.
const BoardReportsView: React.FC<BoardReportsViewProps> = ({ board }) => {
  const queryClient: QueryClient = useQueryClient();
  // Cached by TanStack Query; stale-response ordering is handled by the cache. Reports are
  // derived from the issue set, so recompute only when an issue actually changes —
  // comment/link/attachment/config edits leave the numbers untouched.
  const reportQuery = useQuery({
    queryKey: boardKeys.reports(board.id),
    queryFn: (): Promise<BoardReport> => BoardApi.getReport(board.id)
  });
  const report: BoardReport | null = reportQuery.data ?? null;

  const { config } = useBoardConfig();

  // Burndown: the sprints under this board's projects, newest first; default to the active one.
  // `activeSprintId` falls back to the default so the chart shows once config loads (before the
  // user picks), and lets an explicit selection win afterwards.
  const projectKeys: string[] = boardProjectKeys(board);
  const boardSprints: Sprint[] = config.sprints
    .filter((sprint: Sprint) => projectKeys.includes(sprint.projectKey))
    .sort((a: Sprint, b: Sprint) => b.createdAt.localeCompare(a.createdAt));
  const defaultSprintId: string =
    boardSprints.find((sprint: Sprint) => sprint.state === "active")?.id ?? boardSprints[0]?.id ?? "";
  const [pickedSprintId, setPickedSprintId] = useState<string>("");
  const activeSprintId: string = pickedSprintId || defaultSprintId;
  const burndownQuery = useQuery({
    queryKey: boardKeys.burndown(activeSprintId),
    queryFn: (): Promise<SprintBurndown> => BoardApi.getSprintBurndown(activeSprintId),
    enabled: activeSprintId !== ""
  });
  const burndown: SprintBurndown | null = burndownQuery.data ?? null;

  useEffect(() => {
    return BoardService.subscribe((event: BoardChangeEvent) => {
      if (reportsChanged(event)) {
        void queryClient.invalidateQueries({ queryKey: boardKeys.reports(board.id) });
        if (activeSprintId) void queryClient.invalidateQueries({ queryKey: boardKeys.burndown(activeSprintId) });
      }
    });
  }, [queryClient, board.id, activeSprintId]);

  if (!report || report.total === 0) {
    return (
      <div className="kuika-board-reports kuika-board-reports--empty">
        <div className="kuika-board-reports__empty-card">
          <div className="kuika-board-reports__empty-title">No data yet</div>
          <div className="kuika-board-reports__empty-body">
            Add some issues to this board to see throughput, distribution, and flow metrics here.
          </div>
        </div>
      </div>
    );
  }

  const { total, byCategory, throughput, cumulative, priorityBreakdown } = report;
  const maxThroughput: number = Math.max(1, ...throughput.map((entry) => entry.count));

  return (
    <div className="kuika-board-reports">
      {boardSprints.length > 0 && (
        <div className="kuika-board-reports__row">
          <div className="kuika-board-reports__card kuika-board-reports__card--wide">
            <div className="kuika-board-reports__burndown-head">
              <div>
                <div className="kuika-board-reports__card-title">Sprint burndown</div>
                <div className="kuika-board-reports__card-sub">Remaining work against the ideal trend</div>
              </div>
              <KuikaSelectbox
                style={{ minWidth: 200 }}
                value={activeSprintId}
                onChange={(value: string | number | null) => setPickedSprintId(value ? String(value) : "")}
                options={boardSprints.map((sprint: Sprint) => ({ value: sprint.id, label: sprint.name }))}
              />
            </div>
            <BurndownChart burndown={burndown} loading={burndownQuery.isLoading} />
          </div>
        </div>
      )}
      <div className="kuika-board-reports__row">
        <div className="kuika-board-reports__card">
          <div className="kuika-board-reports__card-title">Flow distribution</div>
          <div className="kuika-board-reports__card-sub">Across {total} issues</div>
          <div className="kuika-board-reports__bar">
            {(["todo", "in_progress", "done"] as const).map((category) => {
              const count: number = byCategory[category] ?? 0;
              const width: number = (count / Math.max(1, total)) * 100;
              return (
                <span
                  key={category}
                  className="kuika-board-reports__bar-segment"
                  style={{ width: `${width}%`, background: STATUS_DOT_COLOR[category] }}
                  title={`${category}: ${count}`}
                />
              );
            })}
          </div>
          <div className="kuika-board-reports__legend">
            {(["todo", "in_progress", "done"] as const).map((category) => (
              <span key={category} className="kuika-board-reports__legend-item">
                <span className="kuika-board-reports__legend-dot" style={{ background: STATUS_DOT_COLOR[category] }} />
                {category.replace("_", " ")}
                <strong>{byCategory[category] ?? 0}</strong>
              </span>
            ))}
          </div>
        </div>

        <div className="kuika-board-reports__card">
          <div className="kuika-board-reports__card-title">Throughput</div>
          <div className="kuika-board-reports__card-sub">Issues completed in the last 8 weeks</div>
          <div className="kuika-board-reports__throughput">
            {throughput.map((entry) => (
              <div key={entry.week} className="kuika-board-reports__throughput-col">
                <div className="kuika-board-reports__throughput-value">
                  <div
                    className="kuika-board-reports__throughput-fill"
                    style={{ height: `${(entry.count / maxThroughput) * 100}%` }}
                  />
                </div>
                <div className="kuika-board-reports__throughput-label">{entry.week}</div>
                <div className="kuika-board-reports__throughput-count">{entry.count}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="kuika-board-reports__row">
        <div className="kuika-board-reports__card kuika-board-reports__card--wide">
          <div className="kuika-board-reports__card-title">Cumulative flow</div>
          <div className="kuika-board-reports__card-sub">Created vs done, running total over 8 weeks</div>
          {(() => {
            const maxValue: number = Math.max(1, ...cumulative.map((entry) => entry.created));
            const width: number = 100;
            const height: number = 40;
            const step: number = cumulative.length > 1 ? width / (cumulative.length - 1) : width;
            const pointFor = (value: number, index: number): string =>
              `${(index * step).toFixed(2)},${(height - (value / maxValue) * height).toFixed(2)}`;
            const createdPoints: string = cumulative.map((entry, i: number) => pointFor(entry.created, i)).join(" ");
            const donePoints: string = cumulative.map((entry, i: number) => pointFor(entry.done, i)).join(" ");
            const createdArea: string = `0,${height} ${createdPoints} ${width},${height}`;
            const doneArea: string = `0,${height} ${donePoints} ${width},${height}`;
            return (
              <div className="kuika-board-reports__cfd">
                <svg
                  viewBox={`0 0 ${width} ${height}`}
                  preserveAspectRatio="none"
                  className="kuika-board-reports__cfd-svg"
                >
                  <polygon points={createdArea} fill="rgba(36, 109, 255, 0.10)" />
                  <polygon points={doneArea} fill="rgba(87, 159, 75, 0.18)" />
                  <polyline
                    points={createdPoints}
                    fill="none"
                    stroke="var(--blue-5)"
                    strokeWidth="0.8"
                    vectorEffect="non-scaling-stroke"
                  />
                  <polyline
                    points={donePoints}
                    fill="none"
                    stroke="var(--green-6)"
                    strokeWidth="0.8"
                    vectorEffect="non-scaling-stroke"
                  />
                </svg>
                <div className="kuika-board-reports__cfd-axis">
                  {cumulative.map((entry) => (
                    <span key={entry.week}>{entry.week}</span>
                  ))}
                </div>
                <div className="kuika-board-reports__legend">
                  <span className="kuika-board-reports__legend-item">
                    <span className="kuika-board-reports__legend-dot" style={{ background: "var(--blue-5)" }} />
                    Created<strong>{cumulative[cumulative.length - 1]?.created ?? 0}</strong>
                  </span>
                  <span className="kuika-board-reports__legend-item">
                    <span className="kuika-board-reports__legend-dot" style={{ background: "var(--green-6)" }} />
                    Done<strong>{cumulative[cumulative.length - 1]?.done ?? 0}</strong>
                  </span>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      <div className="kuika-board-reports__row">
        <div className="kuika-board-reports__card kuika-board-reports__card--wide">
          <div className="kuika-board-reports__card-title">Priority breakdown</div>
          <div className="kuika-board-reports__card-sub">Current open issues</div>
          <table className="kuika-board-reports__priority-table">
            <tbody>
              {priorityBreakdown.map((row) => {
                const pct: number = total === 0 ? 0 : (row.count / total) * 100;
                return (
                  <tr key={row.priority}>
                    <td className="kuika-board-reports__priority-cell">
                      <span
                        className="kuika-board-reports__priority-dot"
                        style={{ background: PRIORITY_COLOR[row.priority] }}
                      />
                      <span className="kuika-board-reports__priority-name">{row.priority}</span>
                    </td>
                    <td className="kuika-board-reports__priority-bar-cell">
                      <div className="kuika-board-reports__priority-bar">
                        <div
                          className="kuika-board-reports__priority-fill"
                          style={{ width: `${pct}%`, background: PRIORITY_COLOR[row.priority] }}
                        />
                      </div>
                    </td>
                    <td className="kuika-board-reports__priority-count">{row.count}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BoardReportsView;
